import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  usePatientCategoryList,
  useCreatePatientCategory,
  useUpdatePatientCategory,
  useDeletePatientCategory,
} from '../../services/httpServices/queries';
import type { PatientCategory } from '../../services/httpServices/patientCategoryService';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  Tags,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  UserPlus,
  Search,
  CheckCircle,
  XCircle,
  Save,
  Calendar,
  Clock,
  Hash,
  FileText,
} from 'lucide-react';

const LIMIT = 20;

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

type StatusFilter = '' | 'active' | 'inactive';

interface CategoryFormState {
  name: string;
  label: string;
  description: string;
  isActive: boolean;
}

const EMPTY_FORM: CategoryFormState = {
  name: '',
  label: '',
  description: '',
  isActive: true,
};

function toSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Small labelled tile used by the details modal. */
function DetailTile({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-background p-3">
      <div className="p-1.5 rounded-lg bg-primary/10 text-primary shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-[0.6875rem] font-medium uppercase tracking-wide text-text-muted">{label}</p>
        <div className="mt-0.5 text-sm text-text break-words">{children}</div>
      </div>
    </div>
  );
}

export function PatientCategories() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const [status, setStatus] = useState<StatusFilter>(() => {
    const value = params.get('status');
    return value === 'active' || value === 'inactive' ? value : '';
  });
  const debouncedSearch = useDebounce(search, 400);

  // Remembers the URL this component last wrote so the URL → state sync below
  // only reacts to real navigation, not to our own writes (which would drop
  // characters while typing).
  const lastWrittenUrlRef = useRef<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PatientCategory | null>(null);
  const [form, setForm] = useState<CategoryFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [viewTarget, setViewTarget] = useState<PatientCategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PatientCategory | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching, error } = usePatientCategoryList({
    page,
    limit: LIMIT,
    search: debouncedSearch.trim() || undefined,
    status: status || undefined,
  });
  const createCategory = useCreatePatientCategory();
  const updateCategory = useUpdatePatientCategory();
  const deleteCategory = useDeletePatientCategory();

  const categories = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);

  const allSelected = categories.length > 0 && categories.every((c) => selectedIds.includes(c.id));
  const someSelected = categories.some((c) => selectedIds.includes(c.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  // Drop selections that are no longer visible (page or filter change).
  useEffect(() => {
    const visible = new Set(categories.map((c) => c.id));
    setSelectedIds((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [categories]);

  // Sync state TO the URL.
  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (status) newParams.set('status', status);

    const query = newParams.toString();
    const newUrl = `${location.pathname}${query ? `?${query}` : ''}`;
    lastWrittenUrlRef.current = newUrl;
    if (newUrl !== `${location.pathname}${location.search}`) {
      navigate(newUrl, { replace: true });
    }
  }, [page, debouncedSearch, status, navigate, location.pathname, location.search]);

  // Sync state FROM the URL (back button, manual URL edit).
  useEffect(() => {
    if (`${location.pathname}${location.search}` === lastWrittenUrlRef.current) return;

    const p = params.get('page');
    const urlPage = p ? Math.max(1, parseInt(p, 10)) : 1;
    const urlSearch = params.get('search') || '';
    const rawStatus = params.get('status');
    const urlStatus: StatusFilter =
      rawStatus === 'active' || rawStatus === 'inactive' ? rawStatus : '';

    if (urlPage !== page) setPage(urlPage);
    if (urlSearch !== search) setSearch(urlSearch);
    if (urlStatus !== status) setStatus(urlStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Close the details modal with Escape.
  useEffect(() => {
    if (!viewTarget) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setViewTarget(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [viewTarget]);

  const hasActiveFilters = Boolean(search || status);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setPage(1);
    setSelectedIds([]);
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : categories.map((c) => c.id));
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setShowForm(true);
  };

  const startEdit = (category: PatientCategory) => {
    setEditing(category);
    setForm({
      name: category.name,
      label: category.label,
      description: category.description || '',
      isActive: category.isActive,
    });
    setFormErrors({});
    setShowForm(true);
  };

  const closeForm = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setShowForm(false);
    setEditing(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: Record<string, string> = {};
    const name = form.name.trim() || toSlug(form.label);
    if (!form.label.trim()) errors.label = 'Label is required';
    if (!name) errors.name = 'Key is required (or add a label to generate one)';
    else if (!/^[a-z0-9_]+$/.test(name)) {
      errors.name = 'Key may only contain lowercase letters, numbers and underscores';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = {
      name,
      label: form.label.trim(),
      description: form.description.trim() || undefined,
      isActive: form.isActive,
    };

    try {
      if (editing) {
        await updateCategory.mutateAsync({ id: editing.id, data: payload });
        toast.success('Patient category updated');
      } else {
        await createCategory.mutateAsync(payload);
        toast.success('Patient category created');
      }
      closeForm();
    } catch (err: any) {
      setFormErrors({
        general: err?.response?.data?.message || err?.message || 'Something went wrong',
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    try {
      await deleteCategory.mutateAsync(target.id);
      toast.success(`"${target.label}" moved to Trash`);
      setSelectedIds((prev) => prev.filter((id) => id !== target.id));
      setDeleteTarget(null);
      // If the last row on a later page was removed, step back a page.
      if (page > 1 && categories.length === 1) setPage(page - 1);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete patient category');
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    let deleted = 0;
    try {
      for (const id of ids) {
        try {
          await deleteCategory.mutateAsync(id);
          deleted += 1;
        } catch {
          // Reported in aggregate below.
        }
      }
      if (deleted > 0) {
        toast.success(`Moved ${deleted} categor${deleted === 1 ? 'y' : 'ies'} to Trash`);
      }
      if (deleted < ids.length) {
        toast.error(`${ids.length - deleted} categor${ids.length - deleted === 1 ? 'y' : 'ies'} could not be deleted`);
      }
      setSelectedIds([]);
    } finally {
      setBulkConfirmOpen(false);
      if (page > 1 && deleted === categories.length) setPage(page - 1);
    }
  };

  const busy = createCategory.isPending || updateCategory.isPending;
  const inputBase =
    'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-colors';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const inputClass = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Tags className="w-6 h-6 shrink-0 text-primary" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-text">Patient Categories</h1>
            <p className="text-sm text-text-muted">
              Categories added here appear in the Patient Category dropdown of the Patient Registration form.
              Inactive categories are hidden there.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/patients/categories/trash"
            className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2 whitespace-nowrap"
            title="View trashed categories"
          >
            <Trash2 size={16} />
            Trash
            {trashedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[0.625rem] font-semibold leading-none">
                {trashedCount > 99 ? '99+' : trashedCount}
              </span>
            )}
          </Link>
          <Link
            to="/patients/new"
            className="px-4 py-2 border border-border text-text-muted rounded-lg hover:bg-background hover:text-text transition-colors text-sm font-medium flex items-center gap-2 whitespace-nowrap"
          >
            <UserPlus size={16} />
            Register Patient
          </Link>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={16} />
            Add Category
          </button>
        </div>
      </div>

      {/* Search & filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by label, key or description..."
              className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
            {isFetching && !isLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-text-muted" />
            )}
          </div>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as StatusFilter);
              setPage(1);
            }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            aria-label="Filter by status"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load patient categories</span>
        </div>
      )}

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={deleteCategory.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {deleteCategory.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={deleteCategory.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={categories.length === 0}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    title="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Label</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Key</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                    {hasActiveFilters ? (
                      <>
                        No categories match your filters.{' '}
                        <button onClick={clearFilters} className="text-primary hover:underline">
                          Clear filters
                        </button>
                      </>
                    ) : (
                      'No patient categories configured. Add your first category to get started.'
                    )}
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(category.id)}
                        onChange={() => toggleOne(category.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Tags size={14} className="text-primary" />
                        </div>
                        <button
                          onClick={() => setViewTarget(category)}
                          className="text-sm font-medium text-text text-left hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
                          title="View details"
                        >
                          {category.label}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-muted font-mono">{category.name}</td>
                    <td className="px-4 py-3 text-sm text-text-muted max-w-xs truncate">
                      {category.description || '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          category.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {category.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(category)}
                          className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(category)}
                          disabled={deleteCategory.isPending}
                          className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                          title="Move to trash"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={meta?.page ?? page}
          totalPages={meta?.totalPages ?? 1}
          total={meta?.total ?? categories.length}
          limit={meta?.limit ?? LIMIT}
          onPageChange={(next) => {
            setPage(next);
            setSelectedIds([]);
          }}
          itemName="categories"
        />
      </div>

      {/* Details modal */}
      {viewTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${viewTarget.label} details`}
          onClick={() => setViewTarget(null)}
        >
          <div
            className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-primary/10 via-surface to-surface px-6 pt-6 pb-5 border-b border-border">
              <button
                onClick={() => setViewTarget(null)}
                className="absolute top-4 right-4 p-1.5 rounded-lg bg-surface/70 hover:bg-background text-text-muted hover:text-text transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/15 ring-1 ring-primary/20 flex items-center justify-center shrink-0">
                  <Tags size={22} className="text-primary" />
                </div>
                <div className="min-w-0 pr-8">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-bold text-text break-words">{viewTarget.label}</h2>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        viewTarget.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                      }`}
                    >
                      {viewTarget.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                      {viewTarget.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-0.5 text-xs font-mono text-text-muted">
                    <Hash size={12} />
                    {viewTarget.name}
                  </span>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              <div>
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
                  <FileText size={14} />
                  Description
                </p>
                <div className="rounded-xl border border-border bg-background p-4 text-sm text-text whitespace-pre-wrap">
                  {viewTarget.description ? (
                    viewTarget.description
                  ) : (
                    <span className="text-text-muted italic">No description provided.</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailTile icon={<CheckCircle size={14} />} label="Status">
                  {viewTarget.isActive ? 'Shown in the patient form' : 'Hidden from the patient form'}
                </DetailTile>
                <DetailTile icon={<Calendar size={14} />} label="Created">
                  {formatDateTime(viewTarget.createdAt)}
                </DetailTile>
                <DetailTile icon={<Clock size={14} />} label="Last updated">
                  {formatDateTime(viewTarget.updatedAt)}
                </DetailTile>
                <DetailTile icon={<Hash size={14} />} label="Category ID">
                  <span className="font-mono text-xs">{viewTarget.id}</span>
                </DetailTile>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-border bg-background/40">
              <button
                onClick={() => {
                  const target = viewTarget;
                  setViewTarget(null);
                  setDeleteTarget(target);
                }}
                disabled={deleteCategory.isPending}
                className="px-3 py-2 rounded-lg text-sm font-medium text-danger border border-danger/30 hover:bg-danger/10 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                <Trash2 size={15} />
                Move to Trash
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewTarget(null)}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    const target = viewTarget;
                    setViewTarget(null);
                    startEdit(target);
                  }}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <Edit2 size={15} />
                  Edit Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => !busy && closeForm()}
        >
          <div
            className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-semibold text-text">
                {editing ? 'Edit Patient Category' : 'New Patient Category'}
              </h2>
              <button
                onClick={closeForm}
                disabled={busy}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {formErrors.general && (
                <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{formErrors.general}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text mb-1">Label *</label>
                <input
                  value={form.label}
                  onChange={(e) => {
                    setForm({ ...form, label: e.target.value });
                    if (formErrors.label) setFormErrors((prev) => ({ ...prev, label: '' }));
                  }}
                  placeholder="e.g. Corporate Plus"
                  className={inputClass('label')}
                />
                {formErrors.label && <p className="text-xs text-danger mt-1">{formErrors.label}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">Key (auto-generated if empty)</label>
                <input
                  value={form.name}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                    if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: '' }));
                  }}
                  placeholder="e.g. corporate_plus"
                  className={`${inputClass('name')} font-mono`}
                />
                {formErrors.name ? (
                  <p className="text-xs text-danger mt-1">{formErrors.name}</p>
                ) : (
                  <p className="text-xs text-text-muted mt-1">
                    Stored on the patient record. Lowercase letters, numbers and underscores only.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description of this category"
                  rows={3}
                  className={`${inputBase} ${inputValid}`}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="pcIsActive"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                />
                <label htmlFor="pcIsActive" className="text-sm text-text">
                  Active (visible in the patient form)
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={busy}
                  className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {editing ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Move patient category to Trash?"
        description={
          <>
            <strong className="text-text">{deleteTarget?.label}</strong> will be removed from the patient
            registration form. You can restore it later from the Trash.
          </>
        }
        confirmLabel="Move to Trash"
        loading={deleteCategory.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Move ${selectedIds.length} categor${selectedIds.length === 1 ? 'y' : 'ies'} to Trash?`}
        description="The selected categories will be removed from the patient registration form. You can restore them later from the Trash."
        confirmLabel={`Delete ${selectedIds.length}`}
        loading={deleteCategory.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
